import { useTranslation } from "react-i18next";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import RootRedirect from "./routes/RootRedirect.jsx";
import ContentRedirect from "./routes/ContentRedirect.jsx";
import LanguageLayout from "./routes/LanguageLayout.jsx";
import ScrollToTop from "./routes/ScrollToTop.jsx";
import TrackPageViews from "./routes/TrackPageViews.jsx";
import OrganizationJsonLd from "./components/shared/OrganizationJsonLd.jsx";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "./i18n.js";
import Home from "./pages/Home.jsx";
import EventList from "./pages/Events/EventList.jsx";
import EventDetail from "./pages/Events/EventDetail.jsx";
import LarpHub from "./pages/Larp/LarpHub.jsx";
import LarpSubpage from "./pages/Larp/LarpSubpage.jsx";
import Impressum from "./pages/Legal/Impressum.jsx";
import Datenschutz from "./pages/Legal/Datenschutz.jsx";
import AGB from "./pages/Legal/AGB.jsx";
import Kontakt from "./pages/Kontakt.jsx";
import Newsletter from "./pages/Newsletter.jsx";
import DonationOverview from "./pages/Spenden/DonationOverview.jsx";
import DonationDetail from "./pages/Spenden/DonationDetail.jsx";
import AboutHub from "./pages/About/AboutHub.jsx";
import AboutSubpage from "./pages/About/AboutSubpage.jsx";
import OrgasPage from "./pages/About/OrgasPage.jsx";
import SupportHub from "./pages/Support/SupportHub.jsx";
import SupportSubpage from "./pages/Support/SupportSubpage.jsx";
import Press from "./pages/Press.jsx";
import Jugendarbeit from "./pages/Jugendarbeit.jsx";
import NotFound from "./pages/NotFound.jsx";

function WithLang({ Component }) {
  const { lang } = useParams();
  return <Component lang={lang} />;
}

function WithLangTopic({ Component, topic }) {
  const { lang } = useParams();
  return <Component lang={lang} topic={topic} />;
}

/** /infos was folded into /kontakt (its FAQ and transparency content moved
 * there and to /ueber-uns) — client-side counterpart to the 301 in
 * scripts/server.cjs for anyone already on the SPA shell. */
function InfosRedirect() {
  const { lang } = useParams();
  return <Navigate to={`/${lang}/${lang === "de" ? "kontakt" : "contact"}`} replace />;
}

/** The three EN slugs that used to carry a redundant "-en" suffix — the
 * /en/ folder already carries the language (§ T-19). Client-side
 * counterpart to the 301s in scripts/server.cjs; these paths are
 * EN-only, so the target language is fixed rather than derived from the
 * :lang segment. */
const RENAMED_EN_PATHS = {
  "experience-larp/faq-en": "/en/experience-larp/faq",
  "experience-larp/larp-settings-en": "/en/experience-larp/larp-settings",
  "donate/wayfarer-2026-en": "/en/donate/wayfarer-2026",
};

/** /unterstuetzen/foerdermitgliedschaft was renamed to /unterstuetzen/foerderer. */
function FoerdererRedirect() {
  const { lang } = useParams();
  return <Navigate to={`/${lang}/unterstuetzen/foerderer`} replace />;
}

export default function App() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  // Derived straight from the URL, not from i18n.language, and applied here
  // (the top of the tree) rather than only in LanguageLayout: this component's
  // own t() calls below (cookie-consent copy) run before React recurses into
  // <Routes>/<LanguageLayout>, so syncing the language any later leaves the
  // very first hydration pass — and this banner specifically — rendering in
  // whatever language happened to be default, mismatching the prerendered
  // per-locale HTML for every /en/ page.
  const urlLang = pathname.split("/")[1];
  const lang = SUPPORTED_LANGUAGES.includes(urlLang) ? urlLang : DEFAULT_LANGUAGE;
  if (i18n.language !== lang) {
    i18n.changeLanguage(lang);
  }

  return (
    <>
      <ScrollToTop />
      <TrackPageViews />
      <OrganizationJsonLd />
      <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/go/:id" element={<ContentRedirect />} />
          <Route path="/:lang" element={<LanguageLayout />}>
            <Route index element={<WithLang Component={Home} />} />
            <Route path="events" element={<WithLang Component={EventList} />} />
            <Route path="events/:slug" element={<WithLang Component={EventDetail} />} />
            <Route path="larp-erleben" element={<WithLang Component={LarpHub} />} />
            <Route path="experience-larp" element={<WithLang Component={LarpHub} />} />
            <Route path="larp-erleben/:slug" element={<WithLang Component={LarpSubpage} />} />
            <Route path="experience-larp/:slug" element={<WithLang Component={LarpSubpage} />} />
            <Route path="impressum" element={<WithLang Component={Impressum} />} />
            <Route path="legal-notice" element={<WithLang Component={Impressum} />} />
            <Route path="datenschutz" element={<WithLang Component={Datenschutz} />} />
            <Route path="privacy" element={<WithLang Component={Datenschutz} />} />
            <Route path="agb" element={<WithLang Component={AGB} />} />
            <Route path="terms" element={<WithLang Component={AGB} />} />
            <Route path="kontakt" element={<WithLang Component={Kontakt} />} />
            <Route path="contact" element={<WithLang Component={Kontakt} />} />
            <Route path="newsletter" element={<WithLang Component={Newsletter} />} />
            <Route path="spenden" element={<WithLang Component={DonationOverview} />} />
            <Route path="donate" element={<WithLang Component={DonationOverview} />} />
            <Route path="spenden/:slug" element={<WithLang Component={DonationDetail} />} />
            <Route path="donate/:slug" element={<WithLang Component={DonationDetail} />} />
            <Route path="ueber-uns" element={<WithLang Component={AboutHub} />} />
            <Route path="about" element={<WithLang Component={AboutHub} />} />
            <Route path="ueber-uns/team" element={<WithLangTopic Component={AboutSubpage} topic="team" />} />
            <Route path="about/team" element={<WithLangTopic Component={AboutSubpage} topic="team" />} />
            <Route path="ueber-uns/gesellschaftsvertrag" element={<WithLangTopic Component={AboutSubpage} topic="charter" />} />
            <Route path="about/charter" element={<WithLangTopic Component={AboutSubpage} topic="charter" />} />
            <Route path="ueber-uns/rueckblick" element={<WithLangTopic Component={AboutSubpage} topic="history" />} />
            <Route path="about/history" element={<WithLangTopic Component={AboutSubpage} topic="history" />} />
            <Route path="ueber-uns/rechenschaftsberichte" element={<WithLangTopic Component={AboutSubpage} topic="reports" />} />
            <Route path="about/accountability-reports" element={<WithLangTopic Component={AboutSubpage} topic="reports" />} />
            <Route path="ueber-uns/verhaltenskodex" element={<WithLangTopic Component={AboutSubpage} topic="conduct" />} />
            <Route path="about/code-of-conduct" element={<WithLangTopic Component={AboutSubpage} topic="conduct" />} />
            <Route path="ueber-uns/jugendschutzkonzept" element={<WithLangTopic Component={AboutSubpage} topic="jugendschutz" />} />
            <Route path="about/youth-protection-concept" element={<WithLangTopic Component={AboutSubpage} topic="jugendschutz" />} />
            <Route path="ueber-uns/fuer-orgas" element={<WithLang Component={OrgasPage} />} />
            <Route path="about/for-organizers" element={<WithLang Component={OrgasPage} />} />
            {/* The section index the three /unterstuetzen/* pages hang
              * off — it 404'd before, while being the natural URL to hand
              * a partner. */}
            <Route path="unterstuetzen" element={<WithLang Component={SupportHub} />} />
            <Route path="support" element={<WithLang Component={SupportHub} />} />
            <Route
              path="unterstuetzen/foerderer"
              element={<WithLangTopic Component={SupportSubpage} topic="membership" />}
            />
            {/* Renamed from "foerdermitgliedschaft" — a gUG has no Mitglieder, so
             * the old URL named a legal relationship that doesn't exist. See
             * scripts/server.cjs's LEGACY_REDIRECTS for the matching 301. */}
            <Route path="unterstuetzen/foerdermitgliedschaft" element={<FoerdererRedirect />} />
            <Route path="support/membership" element={<WithLangTopic Component={SupportSubpage} topic="membership" />} />
            <Route
              path="unterstuetzen/sponsoring"
              element={<WithLangTopic Component={SupportSubpage} topic="sponsoring" />}
            />
            <Route path="support/sponsoring" element={<WithLangTopic Component={SupportSubpage} topic="sponsoring" />} />
            <Route path="unterstuetzen/ehrenamt" element={<WithLangTopic Component={SupportSubpage} topic="volunteer" />} />
            <Route path="support/volunteer" element={<WithLangTopic Component={SupportSubpage} topic="volunteer" />} />
            <Route path="presse" element={<WithLang Component={Press} />} />
            <Route path="press" element={<WithLang Component={Press} />} />
            <Route path="jugendarbeit" element={<WithLang Component={Jugendarbeit} />} />
            <Route path="youth-work" element={<WithLang Component={Jugendarbeit} />} />
            {Object.entries(RENAMED_EN_PATHS).map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            <Route path="infos" element={<InfosRedirect />} />
            <Route path="info" element={<InfosRedirect />} />
            <Route path="*" element={<WithLang Component={NotFound} />} />
          </Route>
      </Routes>
    </>
  );
}
